import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '@/components/molecules/ContentBlock';
import FishHealthFilter from '@/components/molecules/FishHealthFilter';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { FullSizeImage } from '@/domain/images';
import { fullSizeWoundImageMapper } from '@/domain/images';
import { useImageState } from '@/hooks/image-state';

import { getWoundFilterForTag, WOUND_DETECTION_FILTER_TAGS } from '../../_utils/woundFilterTags';

import styles from './HealthWoundInsightDetections.module.scss';

const DETECTIONS_VIEWER_HEIGHT = 480;
export interface HealthWoundInsightDetectionsProps {
    isLoading: boolean;
    imagesData: FullSizeImage[];
    cageId: number;
    timezone: string;
    isOptoScaleAdmin: boolean;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
}

export default function HealthWoundInsightDetections(props: HealthWoundInsightDetectionsProps) {
    const { isLoading, imagesData, cageId, timezone, isOptoScaleAdmin, onCommitFalseDetection } =
        props;
    const { t } = useTranslation('healthModule');

    const filterTagsList = WOUND_DETECTION_FILTER_TAGS;

    const [selectedImageFilterTag, setSelectedImageFilterTag] = useState<string>(
        () => filterTagsList[0]?.tag ?? 'all'
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: mapper must update when cageId changes
    const mapper = useCallback(fullSizeWoundImageMapper(cageId), [cageId]);
    const filter = useMemo(
        () => getWoundFilterForTag(selectedImageFilterTag),
        [selectedImageFilterTag]
    );

    const imageState = useImageState({
        loading: isLoading,
        mapper,
        data: imagesData,
        queryParam: 'imageId',
        timezone,
        filter,
    });

    const imageStateRef = useRef(imageState);
    imageStateRef.current = imageState;

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset timeline only when filter tag changes; state read via ref
    useEffect(() => {
        const state = imageStateRef.current;
        if (!state) {
            return;
        }
        const lastDay = state.dayswithimages.findLast((day) => day.day <= state.selectedImageDate);
        state.onAfterTimelineChangeHandler(lastDay?.day ?? state.dayswithimages.at(0)?.day ?? '');
    }, [selectedImageFilterTag]);

    const [firstSetup, setFirstSetup] = useState(false);

    useEffect(() => {
        if (!firstSetup && imageState) {
            setFirstSetup(true);
            setSelectedImageFilterTag('all');
        }
    }, [firstSetup, imageState]);

    const imageViewerKey = useMemo(() => {
        return `healthWoundInsight-${selectedImageFilterTag}-${imageState?.initialImageIndex ?? 0}`;
    }, [imageState?.initialImageIndex, selectedImageFilterTag]);

    if (!imageState) {
        return null;
    }

    return (
        <ContentBlock title={t('wounds.woundDetections')} size="compact" spacing="none" shrink>
            <div className={styles.detections} data-testid="health-wound-insight-detections">
                <div
                    className={styles.imageWrapper}
                    style={{ minHeight: DETECTIONS_VIEWER_HEIGHT }}>
                    <ImageViewer
                        key={imageViewerKey}
                        variant="healthWoundInsight"
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        onCommitImageFeedback={onCommitFalseDetection}
                        options={{ boxes: 'on', showHelp: false }}
                        images={imageState.images}
                        daysWithImages={imageState.dayswithimages}
                        onDayChange={imageState.onAfterTimelineChangeHandler}
                        initialImageIndex={imageState.initialImageIndex}
                        isFetching={imageState.isFetching}
                    />
                </div>
                <div className={styles.filterOptions}>
                    <FishHealthFilter
                        filterStateList={filterTagsList}
                        onFilterGroupChange={setSelectedImageFilterTag}
                        currentFilterStateList={selectedImageFilterTag}
                    />
                </div>
            </div>
        </ContentBlock>
    );
}
